import { AuthenticationUser, AuthRoleEnum, Base, useLoginValidateMutation } from '@codezo/graphql';
import { Center, Loader, Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { devError, devLog } from '../utils/devLog';
import { scheduleTokenRefresh } from '../utils/tokenScheduler';
import { storeUserRole } from '../utils/tokenStorage';

export interface AuthCallbackPageProps<TUser extends Base> {
	/**
	 * Required role(s) for this platform
	 * Can be a single role or array of roles
	 */
	requiredRole: AuthRoleEnum | AuthRoleEnum[];
	/**
	 * Where to redirect after successful authentication
	 * @default '/dashboard'
	 */
	successRedirect?: string;
	/**
	 * Set user and access token in context
	 */
	setUser: (user: AuthenticationUser) => void;
	/**
	 * Set access token in context
	 */
	setAccessToken: (token: string) => void;
}

export function AuthCallbackPage<TUser extends Base>({
	requiredRole,
	successRedirect = '/dashboard',
	setUser,
	setAccessToken,
}: AuthCallbackPageProps<TUser>) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const validationInitiated = useRef(false);

	const [loginValidate] = useLoginValidateMutation({
		onError: (err) => {
			devLog(err.message || 'Authentication validation failed.');
			navigate('/404');
		},
	});

	useEffect(() => {
		// Prevent double execution
		if (validationInitiated.current) {
			return;
		}

		const validateLogin = async () => {
			const token = searchParams.get('token');

			if (!token) {
				navigate('/404');
				return;
			}

			// Mark validation as initiated immediately
			validationInitiated.current = true;

			try {
				const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

				const result = await loginValidate({
					variables: {
						input: {
							token,
							role: roles,
						},
					},
				});

				if (result.data?.loginValidate) {
					const { user, token } = result.data.loginValidate;

					// Check if user has required role
					if (!roles.includes(user.role)) {
						const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
						window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
						return;
					}
					// Set user and access token in context
					setUser(user as AuthenticationUser);
					setAccessToken(token);

					// Refresh token now travels as an HttpOnly cookie set by the server
					// during the loginValidate response — nothing for the frontend to store.

					// Store user role for platform routing
					storeUserRole(user.role);

					// Schedule automatic token refresh
					scheduleTokenRefresh(token);

					// Navigate to success redirect
					navigate(successRedirect, { replace: true });
				}
			} catch (err) {
				devError('Validation error:', err);
				// Error is already handled by onError callback
			}
		};

		validateLogin();
	}, []);

	return (
		<Center h="100vh">
			<Stack align="center" gap="lg">
				<Loader size="lg" color="codezo" />
				<Stack align="center" gap="xs">
					<Text size="lg" fw={500}>
						Authenticating...
					</Text>
					<Text size="sm" c="dimmed">
						Please wait while we validate your credentials.
					</Text>
				</Stack>
			</Stack>
		</Center>
	);
}
